<template>
  <div>
    <template v-if="inlineResult">
      <BulkImportResult
        :import-data="inlineResult"
        :embedded="true"
        @back="clearInlineResult"
        @retry="clearInlineResult"
      />
    </template>
    <template v-else>
      <breadcrumb :items="breadcrumbs"></breadcrumb>
      <v-container fluid>
        <div class="d-flex mb-4">
          <h2 class="font-weight-bolder">{{ $t('memberDataSection.parchmentDelivery.bulkImport.title') }}</h2>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined class="mr-2" @click="goBack">
            {{ $t('back') }}
          </v-btn>
        </div>

        <v-card elevation="0" class="px-5">
          <v-card elevation="0" class="green-coffee-bulk-tabs-card">
            <v-tabs
              v-model="tab"
              class="green-coffee-bulk-tabs"
              background-color="transparent"
              :grow="false"
            >
              <v-tab class="text-capitalize">{{ $t('oma.import') }}</v-tab>
              <v-tab class="text-capitalize">{{ $t('oma.history') }}</v-tab>
            </v-tabs>
          </v-card>

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <BulkImportTab
                @cancelImport="goBack"
                @importSuccess="onImportSuccess"
              />
            </v-tab-item>
            <v-tab-item>
              <v-card elevation="0" flat>
                <v-data-table
                  :headers="historyHeaders"
                  :items="historyList"
                  :loading="historyLoading"
                  :server-items-length="historyTotal"
                  :options.sync="historyOptions"
                  :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
                  class="elevation-0"
                >
                  <template v-slot:no-data>
                    <span class="text--secondary">{{ $t('memberDataSection.parchmentDelivery.bulkImport.historyEmpty') }}</span>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip
                      small
                      :color="item.status === 'completed' ? 'success' : item.status === 'partial' ? 'warning' : 'error'"
                      text-color="white"
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-slot:item.s3FileUrl="{ item }">
                    <v-btn
                      v-if="item.s3FileUrl"
                      icon
                      small
                      :href="item.s3FileUrl"
                      target="_blank"
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <span v-else class="text--disabled">—</span>
                  </template>
                  <template v-slot:item.createdAt="{ item }">
                    {{ item.createdAt | formatDate }}
                  </template>
                </v-data-table>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-container>
    </template>
  </div>
</template>

<script>
import BulkImportTab from "./components/bulk-import/BulkImportTab.vue";
import BulkImportResult from "./components/bulk-import/BulkImportResult.vue";
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";

export default {
  name: "CoffeeBuyingStationParchmentDeliveriesBulkImport",
  components: { BulkImportTab, BulkImportResult },
  data() {
    return {
      tab: 0,
      inlineResult: null,
      historyList: [],
      historyLoading: false,
      historyTotal: 0,
      historyOptions: { page: 1, itemsPerPage: 20 },
      historyHeaders: [
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyFileName'), value: 'originalFileName', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyTotal'), value: 'totalRecords', align: 'center', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historySuccess'), value: 'successRecords', align: 'center', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyIssues'), value: 'issuesRecords', align: 'center', sortable: false },
        { text: this.$t('oma.status'), value: 'status', align: 'center', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyImportedBy'), value: 'importedByName', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyDate'), value: 'createdAt', sortable: false },
        { text: this.$t('memberDataSection.parchmentDelivery.bulkImport.historyFile'), value: 's3FileUrl', align: 'center', sortable: false },
      ],
      breadcrumbs: [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("memberDataSection.buyingStation"), disabled: false, to: { name: "CoffeeBuyingStation" }, exact: true },
        {
          text: this.$t("memberDataSection.parchmentDelivery.listTitle"),
          disabled: false,
          to: { name: "CoffeeBuyingStationParchmentDeliveries" },
          exact: true,
        },
        { text: this.$t("memberDataSection.parchmentDelivery.bulkImport.title"), disabled: true, href: "#" },
      ],
    };
  },
  created() {
    this.syncTabFromRoute();
  },
  watch: {
    "$route.query.tab"() {
      this.syncTabFromRoute();
    },
    tab(val) {
      if (val === 1) this.fetchHistory();
    },
    historyOptions() {
      if (this.tab === 1) this.fetchHistory();
    },
  },
  methods: {
    syncTabFromRoute() {
      const tab = this.$route?.query?.tab;
      this.tab = tab === "history" || tab === "1" ? 1 : 0;
    },
    async fetchHistory() {
      this.historyLoading = true;
      try {
        const { page, itemsPerPage } = this.historyOptions;
        const res = await ParchmentDeliveryService.getBulkImportHistory({ page, limit: itemsPerPage });
        this.historyList  = res?.data?.rows  || [];
        this.historyTotal = res?.data?.totalRows || 0;
      } catch (err) {
        this.$notify({ type: 'error', text: this.$t('somethingWentWrong') });
      } finally {
        this.historyLoading = false;
      }
    },
    goBack() {
      this.$router.push({ path: "/coffee/buying-station" });
    },
    onImportSuccess(detail) {
      this.inlineResult = detail;
    },
    clearInlineResult() {
      this.inlineResult = null;
      this.tab = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.green-coffee-bulk-tabs-card {
  border-bottom: 1px solid #e0e0e0;
}

.green-coffee-bulk-tabs {
  ::v-deep .v-tabs-bar {
    background-color: #fff !important;
  }

  ::v-deep .v-slide-group__content {
    justify-content: flex-start;
  }

  ::v-deep .v-tab {
    text-transform: none;
    letter-spacing: 0;
    min-width: 100px;
    max-width: none;
    color: #787878;
    background-color: transparent;
    font-weight: 400;
  }

  ::v-deep .v-tab--active {
    color: var(--primary-color, #944a00) !important;
    background-color: #eeeeee !important;
    font-weight: 600;
  }

  ::v-deep .v-tabs-slider {
    background-color: var(--primary-color, #944a00) !important;
    height: 2px;
  }
}
</style>

